import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
} from '@/lib/graphql-query-builder';

type Where = Record<string, unknown>;

const list = (
  alias: string,
  schema: string,
  where: Where,
  pageSize: number,
  fields: string,
  order = '[]'
) => `
  query {
    ${alias}: get${schema}s(
      where: ${buildWhereLiteral(where)}
      order: ${order}
      paging: ${buildPagingLiteral(1, pageSize)}
    ) {
      items {
        ${fields}
      }
    }
  }
`;

/** The employee's HR terms, newest first: what the term picker offers. */
export const buildGetHrTermsForLeaveSheetQuery = (where: Where, pageSize: number) =>
  list(
    'HrProfiles',
    'UserHRPeriodicalProfile',
    where,
    pageSize,
    `ItemId
        HRPeriod
        HRPeriodName
        HRPeriodStartDate
        HRPeriodEndDate
        IsCurrentHrYear
        ToilCarryForwardDays
        SpecialToilCarryForwardDays`,
    buildOrderLiteral([{ field: 'HRPeriodStartDate', direction: 'DESC' }])
  );

/** What the ledger (and the allotment balance) reads of an HR term. */
const TERM_DETAIL_FIELDS = `ItemId
        UserId
        HRPeriod
        HRPeriodName
        HRPeriodStartDate
        HRPeriodEndDate
        IsCurrentHrYear
        LastUpdatedDate
        EmploymentType
        IsFlexible
        LeaveCarryForwardDays
        ToilCarryForwardDays
        SpecialToilCarryForwardDays
        WorkDays {
          WorkDay
          EffectiveFrom
        }
        LeaveAllotments {
          LeaveCategoryId
          LeaveCategory
          LeaveLimitTotalDays
          IsApplicable
          IsUnlimited
          IsDefault
        }`;

/** One HR term with everything the ledger reads from it. */
export const buildGetHrTermDetailForLeaveSheetQuery = (where: Where) =>
  list('HrProfiles', 'UserHRPeriodicalProfile', where, 1, TERM_DETAIL_FIELDS);

/** `Pending` and `Approved` leave requests overlapping the term. */
export const buildGetLeavesForSheetQuery = (where: Where, pageSize: number) =>
  list(
    'LeaveRequests',
    'LeaveRequest',
    where,
    pageSize,
    `Title
        LeaveCategoryId
        LeaveType
        StartDate
        EndDate
        ToilCategory
        Status
        LastUpdatedDate`
  );

/** `Approved` TOIL requests in the term. */
export const buildGetToilsForSheetQuery = (where: Where, pageSize: number) =>
  list(
    'ToilRequests',
    'ToilRequest',
    where,
    pageSize,
    `ToilDate
        ToilType
        ToilCategory`
  );

/** The employee's attendance days in the term (only read under the missed-attendance rule). */
export const buildGetAttendancesForSheetQuery = (where: Where, pageSize: number) =>
  list(
    'Attendances',
    'Attendance',
    where,
    pageSize,
    `AttendanceDate
        StartTimeUtc
        EndTimeUtc
        Timezone
        IsOpen
        TotalClockTimeInSecond
        ExpectedWorkingHourInSecond`
  );

/** The employee's `Pending` attendance corrections in the term (only read under the missed-attendance
 * rule). */
export const buildGetPendingCorrectionsForSheetQuery = (where: Where, pageSize: number) =>
  list('AttendanceCorrections', 'AttendanceCorrection', where, pageSize, `CorrectionDate`);

/** The organization's holidays in the term. */
export const buildGetHolidaysForSheetQuery = (where: Where, pageSize: number) =>
  list('Holidays', 'Holiday', where, pageSize, `StartDate`);

/** All of an employee's HR terms with everything the ledger reads (the review dialog's allotment balance picks
 * the term itself, `allotmentTermOn`). */
export const buildGetHrTermsWithDetailQuery = (where: Where, pageSize: number) =>
  list('HrProfiles', 'UserHRPeriodicalProfile', where, pageSize, TERM_DETAIL_FIELDS);
